import React, { useState, useEffect } from 'react';

import toast from 'react-hot-toast';
import { CreditCard, Loader2 } from 'lucide-react';

import { Button } from '@/components/ui/button';

import { useAuth } from '../../../contexts/AuthContext';
import { billingApi, PaymentMethod } from '../../../lib/api/billing';

interface PmHandoverBannerProps {
  /** Called after "Keep current card" succeeds so the parent can refetch state */
  onDismissed: () => void;
}

/**
 * Payment-Method Handover Card (billing page detail surface)
 * Shown to the NEW owner after a paid-workspace ownership transfer while the
 * previous owner's payment method is still on file. The app-wide slim bar
 * (components/billing/pm-handover-bar.tsx) mirrors the same actions; this card
 * adds detail (which card is on file). Brand-primary styling — semantic amber
 * stays reserved for real failures like past-due.
 */
export function PmHandoverBanner({ onDismissed }: PmHandoverBannerProps) {
  const [addCardLoading, setAddCardLoading] = useState(false);
  const [dismissLoading, setDismissLoading] = useState(false);
  const [cardOnFile, setCardOnFile] = useState<PaymentMethod | null>(null);
  const { getIdToken } = useAuth();

  useEffect(() => {
    let cancelled = false;
    const loadCardOnFile = async (): Promise<void> => {
      try {
        const token = await getIdToken();
        if (!token) return;
        const { paymentMethods, invoiceDefaultId } =
          await billingApi.getPaymentMethods(token);
        if (cancelled) return;
        const current =
          paymentMethods.find((pm) => pm.id === invoiceDefaultId) ||
          paymentMethods[0] ||
          null;
        setCardOnFile(current);
      } catch {
        // Detail line only — the card is still actionable without it
      }
    };
    void loadCardOnFile();
    return () => {
      cancelled = true;
    };
  }, [getIdToken]);

  const handleAddCard = async (
    event: React.MouseEvent<HTMLButtonElement>,
  ): Promise<void> => {
    event.preventDefault();
    try {
      setAddCardLoading(true);
      const token = await getIdToken();
      if (!token) {
        toast.error('Authentication required');
        return;
      }
      const { url } = await billingApi.addCard(token);
      window.location.href = url;
    } catch (error: unknown) {
      const err = error as { response?: { data?: { message?: string } } };
      toast.error(err.response?.data?.message || 'Failed to start add-card flow');
      setAddCardLoading(false);
    }
  };

  const handleKeepCurrentCard = async (
    event: React.MouseEvent<HTMLButtonElement>,
  ): Promise<void> => {
    event.preventDefault();
    try {
      setDismissLoading(true);
      const token = await getIdToken();
      if (!token) {
        toast.error('Authentication required');
        return;
      }
      await billingApi.dismissPmHandover(token);
      toast.success('Keeping the current card on file');
      onDismissed();
    } catch (error: unknown) {
      const err = error as { response?: { data?: { message?: string } } };
      toast.error(err.response?.data?.message || 'Failed to dismiss');
    } finally {
      setDismissLoading(false);
    }
  };

  const brandLabel = cardOnFile
    ? cardOnFile.brand.charAt(0).toUpperCase() + cardOnFile.brand.slice(1)
    : null;

  return (
    <div className="flex gap-4 rounded-xl border border-primary/30 bg-primary/10 p-4">
      <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg bg-primary text-primary-foreground">
        <CreditCard className="h-5 w-5" />
      </div>
      <div className="min-w-0 flex flex-col gap-1">
        <span className="text-sm font-semibold">
          Billing is now your responsibility
        </span>
        <p className="text-sm text-muted-foreground">
          Ownership was transferred to you. Future invoices and receipts already
          go to your email — but the subscription still charges the previous
          owner's card.
        </p>
        {cardOnFile && (
          <p className="text-xs text-muted-foreground">
            On file:{' '}
            <span className="font-medium tabular-nums text-foreground">
              {brandLabel} ·· {cardOnFile.last4}
            </span>{' '}
            — added by the previous owner
          </p>
        )}
        <div className="mt-2 flex max-w-sm items-center gap-2">
          <Button
            size="sm"
            onClick={handleAddCard}
            disabled={addCardLoading || dismissLoading}
            className="h-8 flex-1 border border-transparent text-xs"
          >
            {addCardLoading && (
              <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
            )}
            Add my card
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={handleKeepCurrentCard}
            disabled={addCardLoading || dismissLoading}
            className="h-8 flex-1 text-xs"
          >
            {dismissLoading && (
              <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
            )}
            Keep current card
          </Button>
        </div>
      </div>
    </div>
  );
}
